/**
 * Pure math for the customer-facing receipt / session summary.
 *
 * All amounts in DOLLARS (matches SessionSummary's downstream
 * formatCurrency call).
 *
 * Inputs:
 *   selectedBudget        — the customer's chosen pre-auth amount.
 *   appliedCredit         — the redeem-code value applied (0 if none).
 *   finalEnergyCost       — the actual metered cost of the session.
 *
 * Outputs:
 *   cardAuthNeeded        — max(0, budget - credit).
 *                           What Fiserv was asked to hold.
 *   paidByCredit          — min(finalCost, credit).
 *                           Portion of the final bill covered by the
 *                           redeem code.
 *   paidByCard            — max(0, finalCost - paidByCredit).
 *                           Portion of the final bill actually
 *                           charged to the card.
 *   releasedAuthorization — max(0, cardAuthNeeded - paidByCard).
 *                           What Fiserv holds back (the unused
 *                           portion of the pre-auth) and returns
 *                           to the customer within a few days.
 *   unusedCredit          — 0 for single-use codes (the current
 *                           model). Exposed for forward compat if
 *                           the product later allows carrying
 *                           balance.
 *   hasCredit             — true when appliedCredit > 0.
 *
 * The single-use model has no "leftover credit" — once the code is
 * consumed, its remaining value is forfeited. This is documented in
 * the pre-auth screen's "will not carry forward" hint.
 */
export function computeReceiptBreakdown({
  selectedBudget = 0,
  appliedCredit = 0,
  finalEnergyCost = 0,
} = {}) {
  const budget = Math.max(0, Number(selectedBudget)   || 0);
  const credit = Math.max(0, Number(appliedCredit)    || 0);
  const cost   = Math.max(0, Number(finalEnergyCost)  || 0);

  const cardAuthNeeded        = Math.max(0, budget - credit);
  const paidByCredit          = Math.min(cost, credit);
  const paidByCard            = Math.max(0, cost - paidByCredit);
  const releasedAuthorization = Math.max(0, cardAuthNeeded - paidByCard);

  return {
    selectedBudget:        budget,
    appliedCredit:         credit,
    cardAuthNeeded,
    paidByCredit,
    paidByCard,
    releasedAuthorization,
    unusedCredit:          0, // single-use; leftover value is forfeited
    hasCredit:             credit > 0,
  };
}
